import React, { useEffect, useState } from 'react';
import { DropdownButton, MenuItem } from 'react-bootstrap';
import _ from 'lodash';
import { humanize, titleize } from 'underscore.string';
import { sluggify } from 'utils/localization/utils';
import EditButton from 'components/v2/buttons/EditButton';
import DeleteButton from 'components/v2/buttons/DeleteButton';
import CargomaticCard from 'components/v2/card';
import CargomaticInput from 'components/v2/input';
import CargomaticModal from 'components/v2/modal';
import CargomaticTooltip from 'components/v2/Tooltip';
import AddButton from 'components/v2/buttons/AddButton';
import CustomerSearch from 'components/site/customer-search-alt';
import TagEditor from 'components/ui/TagEditor';
import CustomersAPIUtils from 'utils/CustomersAPIUtils';
import UserWebAPIUtils from 'utils/UserWebAPIUtils';
import { carrierScacs, productTypes } from 'config/shipper-config-items';
import { unescapeSpecialChars } from 'helpers/helper_functions';
import { useTranslation, withTranslation } from 'react-i18next';
const accountNames = {};
const setAccountName = async (account) => {
    if (account && !accountNames[account]) {
        try {
            const shipper = await UserWebAPIUtils.getV2(account);
            accountNames[account] = unescapeSpecialChars(shipper.company_name);
        }
        catch (e) {
            console.log('error getting account', account, 'err:', e.message);
        }
    }
};
const getMapping = (scacCode, setMapping) => {
    // eslint-disable-next-line
    return new Promise(async (resolve, reject) => {
        try {
            const mapping = await CustomersAPIUtils.getShipperAccountRouting(scacCode).catch((e) => {
                throw e;
            });
            if (mapping) {
                setMapping(mapping);
                setAccountName(mapping.defaultShipper);
                resolve(mapping);
            }
            resolve(null);
        }
        catch (e) {
            reject(e);
        }
    });
};
const ShipperRoutingView = (props) => {
    const { scac_code: scacCode } = props.shipper;
    const [mapping, setMapping] = useState(null);
    const [loading, setLoading] = useState(true);
    const { t } = useTranslation();
    useEffect(() => {
        // on component mount
        try {
            if (scacCode) {
                getMapping(scacCode, setMapping);
            }
            setLoading(false);
        }
        catch (e) {
            console.log(e.message);
            setLoading(false);
        }
        return () => {
            // on component unmount
            setMapping(null);
        };
    }, [loading, scacCode]);
    const createMapping = () => {
        const mapping = {
            scacCode,
            defaultShipper: '',
            priority: ['market'],
            customMapping: { market: {} },
        };
        setMapping(mapping);
    };
    const handleChangeMapping = (mapping) => {
        setMapping(mapping);
    };
    const handleSaveMapping = async () => {
        try {
            if (mapping._id) {
                const res = await CustomersAPIUtils.updateShipperAccountRouting(mapping).catch((e) => {
                    throw new Error(e.message || e.status.text);
                });
            }
            else {
                const res = await CustomersAPIUtils.createShipperAccountRouting(mapping).catch((e) => {
                    throw new Error(e.message || e.status.text);
                });
            }
        }
        catch (e) {
            console.log('Error saving:', e.message);
        }
    };
    const isInvalid = () => {
        return false;
    };
    const hasMapping = !_.isEmpty(mapping);
    return (React.createElement(CargomaticCard, { header: () => (React.createElement("div", { className: "flex justify-between" },
            React.createElement("h3", { className: "my-0 text-xl font-medium" }, t('ACCOUNT_ROUTING')),
            React.createElement("div", { className: "flex flex-row" },
                !hasMapping && !loading && !scacCode && (React.createElement(CargomaticTooltip, { id: "noScacTooltip", position: "top", className: "ml-1" }, t('SCAC_MUST_FOR_ROUTING_TIP'))),
                !hasMapping && !loading && (React.createElement(AddButton, { disabled: !scacCode, onClick: createMapping }))))), footer: () => hasMapping ? (React.createElement("div", { className: "flex justify-between" },
            React.createElement("button", { className: "button button-green", disabled: isInvalid(), onClick: handleSaveMapping }, titleize(t('SAVE'))))) : null, grayFooter: true },
        hasMapping && (React.createElement(ShipperRouting, { mapping: mapping, markets: props.markets, onChange: handleChangeMapping, t: t })),
        !scacCode && React.createElement("span", null, t('CUSTOMER_HAS_NO_SCAC')),
        loading && React.createElement("span", null, t('SEARCH_FOR_MAPPING_SCAC', { scacCode })),
        !hasMapping && !loading && scacCode && (React.createElement("span", null, t('MAPPING_FOR_SCAC', { scacCode })))));
};
const ShipperRouting = (props) => {
    if (_.isEmpty(props.mapping)) {
        return null;
    }
    const { t } = props;
    const { scacCode, defaultShipper, priority } = props.mapping;
    let { customMapping } = props.mapping;
    const allPriorities = [
        { label: 'market' },
        { label: 'carrier_scac' },
        { label: 'product_type' },
    ];
    const handleSelectDefaultShipper = (name, shipper) => {
        const mapping = Object.assign(Object.assign({}, props.mapping), { defaultShipper: shipper._id.toString() });
        if (!accountNames[shipper._id.toString()]) {
            accountNames[shipper._id.toString()] = unescapeSpecialChars(shipper.name);
        }
        props.onChange(mapping);
    };
    const handlePriorityChange = (mapping, priority) => {
        if (priority.length &&
            (!mapping.priority.length || priority[0] !== mapping.priority[0])) {
            // Should only be changing priority when there is no customMapping,
            // other than the default, which is now changing.
            customMapping = { [priority[0]]: {} };
        }
        else if (!priority.length) {
            customMapping = {};
        }
        const updatedMapping = Object.assign(Object.assign({}, props.mapping), { priority, customMapping });
        props.onChange(updatedMapping);
    };
    const hasCustomMapping = !_.isEmpty(customMapping) && !_.isEmpty(customMapping[priority[0]]);
    return (React.createElement("div", { className: "flex flex-col w-full space-y-4" },
        React.createElement(CargomaticInput, { label: t('SCAC_CODE'), disabled: true, value: scacCode }),
        React.createElement("div", null,
            React.createElement("label", { className: "mb-2 form-label" }, `${t('DEFAULT_SHIPPER')} *`),
            React.createElement(CustomerSearch, { id: "mappings-shipper-search", clearOnSelect: false, assignField: true, onSelect: handleSelectDefaultShipper, value: accountNames[defaultShipper] || defaultShipper, placeholder: t('SEARCH_FOR_SHIPPER_PLACEHOLDER'), type: "booking_shippers" })),
        React.createElement("div", null,
            React.createElement("label", { className: "mb-2 form-label" }, `${t('ROUTING_PRIORITY_TIP')} *`),
            !hasCustomMapping && (React.createElement(TagEditor, { client: "shipper-mapping", tags: priority, suggestions: allPriorities, object: props.mapping, handleChange: handlePriorityChange, hideLabel: false })),
            hasCustomMapping && React.createElement("span", { className: "ml-3" }, priority.join(' '))),
        React.createElement("div", null,
            React.createElement("label", { className: "mb-2 form-label" }, `${t('ACCOUNT_MAPPING')} *`),
            React.createElement(RoutingLevel, { level: 0, path: [], mapping: props.mapping, markets: props.markets, onChange: props.onChange, t: t }))));
};
const RoutingLevel = (props) => {
    const { level, path, mapping, t } = props;
    const { priority, customMapping } = mapping;
    const key = priority[level];
    const [showModal, setShowModal] = useState(false);
    const [modalType, setModalType] = useState('');
    const [modalAction, setModalAction] = useState('');
    const [modalPath, setModalPath] = useState([]);
    const [selectedMarket, setSelectedMarket] = useState(null);
    const [selectedCarrierScac, setSelectedCarrierScac] = useState(null);
    const [selectedProduct, setSelectedProduct] = useState(null);
    const [selectedAccount, setSelectedAccount] = useState(null);
    const isBottomLevel = () => {
        return level === priority.length - 1;
    };
    const addConfigItem = (key, path) => {
        setModalPath([...path]);
        setModalType(key);
        setModalAction('add');
        setShowModal(true);
    };
    const editConfigItem = (key, path) => {
        setModalPath([...path]);
        setModalType(key);
        setModalAction('edit');
        setShowModal(true);
    };
    const deleteConfigItem = (key, path) => {
        setModalPath([...path]);
        setModalType(key);
        setModalAction('delete');
        setShowModal(true);
    };
    const handleMarketSelect = (selection) => {
        setSelectedMarket(selection);
    };
    const handleCarrierScacSelect = (selection) => {
        setSelectedCarrierScac(selection);
    };
    const handleProductTypeSelect = (selection) => {
        setSelectedProduct(selection);
    };
    const handleAccountSelect = (name, shipper) => {
        setSelectedAccount(shipper);
        if (!accountNames[shipper._id.toString()]) {
            accountNames[shipper._id.toString()] = unescapeSpecialChars(name);
        }
    };
    const renderMarket = () => {
        const marketMenuItems = props.markets
            .sort((a, b) => a.name.localeCompare(b.name))
            .map((market, i) => {
            return (React.createElement(MenuItem, { key: i, eventKey: market.code }, `${market.name} (${market.code})`));
        });
        return (React.createElement("div", { className: "mb-4" },
            React.createElement("label", { className: "block mb-1 form-label" }, titleize(t('MARKET', { count: 1 }))),
            React.createElement("div", { className: "flex flex-row" },
                React.createElement(DropdownButton, { title: selectedMarket ||
                        (modalAction === 'edit' ? _.last(modalPath) : t('SELECT_MARKET')), onSelect: handleMarketSelect }, marketMenuItems),
                isBottomLevel() && (React.createElement(CustomerSearch, { id: "add-product-shipper-search", clearOnSelect: false, assignField: true, onSelect: handleAccountSelect, value: selectedAccount && selectedAccount.name
                        ? selectedAccount.name
                        : accountNames[selectedAccount] || selectedAccount, placeholder: t('SEARCH_FOR_SHIPPER_PLACEHOLDER'), type: "booking_shippers" })))));
    };
    const renderCarrierScac = () => {
        const carrierScacMenuItems = carrierScacs.map((scac) => {
            return (React.createElement(MenuItem, { key: scac, eventKey: scac }, scac));
        });
        return (React.createElement("div", { className: "mb-4" },
            React.createElement("label", { className: "block mb-1 form-label" }, t('CARRIER_SCAC')),
            React.createElement("div", { className: "flex flex-row" },
                React.createElement(DropdownButton, { title: selectedCarrierScac ||
                        (modalAction === 'edit'
                            ? _.last(modalPath)
                            : t('SELECT_CARRIER_SCAC')), onSelect: handleCarrierScacSelect }, carrierScacMenuItems),
                isBottomLevel() && (React.createElement(CustomerSearch, { id: "add-product-shipper-search", clearOnSelect: false, assignField: true, onSelect: handleAccountSelect, value: selectedAccount && selectedAccount.name
                        ? selectedAccount.name
                        : accountNames[selectedAccount] || selectedAccount, placeholder: t('SEARCH_FOR_SHIPPER'), type: "booking_shippers" })))));
    };
    const renderProductType = () => {
        const productTypeMenuItems = productTypes.map((type) => {
            return (React.createElement(MenuItem, { key: type, eventKey: type }, t(sluggify(type))));
        });
        return (React.createElement("div", { className: "mb-4" },
            React.createElement("label", { className: "block mb-1 form-label" }, t('PRODUCT_TYPE')),
            React.createElement("div", { className: "flex flex-row" },
                React.createElement(DropdownButton, { title: selectedProduct ||
                        (modalAction === 'edit'
                            ? _.last(modalPath)
                            : t('SELECT_PRODUCT_TYPE')), onSelect: handleProductTypeSelect }, productTypeMenuItems),
                isBottomLevel() && (React.createElement(CustomerSearch, { id: "add-product-shipper-search", clearOnSelect: false, assignField: true, onSelect: handleAccountSelect, value: selectedAccount && selectedAccount.name
                        ? selectedAccount.name
                        : accountNames[selectedAccount] || selectedAccount, placeholder: t('SEARCH_FOR_SHIPPER_PLACEHOLDER'), type: "booking_shippers" })))));
    };
    const renderDeleteConfirmation = () => {
        return (React.createElement("div", { className: "mb-4" }, t('CONFIRM_DELETION_OF_PATH', { path: modalPath.join(' ') })));
    };
    const renderModalComponent = () => {
        if (modalAction === 'delete') {
            return renderDeleteConfirmation();
        }
        switch (modalType) {
            case 'market':
                return renderMarket();
            case 'carrier_scac':
                return renderCarrierScac();
            case 'product_type':
                return renderProductType();
            default:
                return null;
        }
    };
    const onHideModal = () => {
        setShowModal(false);
    };
    const addFromModal = () => {
        const subTree = modalPath.length
            ? _.get(customMapping, modalPath)
            : customMapping;
        switch (modalType) {
            case 'market':
                if (isBottomLevel()) {
                    subTree.market[selectedMarket] = selectedAccount._id.toString();
                }
                else {
                    subTree.market[selectedMarket] = { [priority[level + 1]]: {} };
                }
                break;
            case 'carrier_scac':
                if (isBottomLevel()) {
                    subTree.carrier_scac[selectedCarrierScac] =
                        selectedAccount._id.toString();
                }
                else {
                    subTree.carrier_scac[selectedCarrierScac] = {
                        [priority[level + 1]]: {},
                    };
                }
                break;
            case 'product_type':
                if (isBottomLevel()) {
                    subTree.product_type[selectedProduct] =
                        selectedAccount._id.toString();
                }
                else {
                    subTree.product_type[selectedProduct] = { [priority[level + 1]]: {} };
                }
                break;
            default:
                break;
        }
        onHideModal();
    };
    const editFromModal = () => {
        const subTree = modalPath.length
            ? _.get(customMapping, modalPath)
            : customMapping;
        const setPoint = _.get(customMapping, modalPath.slice(0, -1));
        const oldValue = _.last(modalPath);
        let key = null;
        switch (modalType) {
            case 'market':
                key = selectedMarket || oldValue;
                break;
            case 'carrier_scac':
                key = selectedCarrierScac || oldValue;
                break;
            case 'product_type':
                key = selectedProduct || oldValue;
                break;
            default:
                break;
        }
        if (key) {
            if (isBottomLevel()) {
                setPoint[key] = selectedAccount._id.toString();
            }
            else {
                setPoint[key] = subTree;
            }
            if (key !== oldValue) {
                delete setPoint[oldValue];
            }
        }
        onHideModal();
    };
    const deleteFromModal = () => {
        const setPoint = _.get(customMapping, modalPath.slice(0, -1));
        const oldValue = _.last(modalPath);
        switch (modalType) {
            case 'market':
                delete setPoint[oldValue];
                break;
            case 'carrier_scac':
                delete setPoint[oldValue];
                break;
            case 'product_type':
                delete setPoint[oldValue];
                break;
            default:
                break;
        }
        onHideModal();
    };
    const renderModalFooter = () => {
        const modalActionReq = modalAction === 'edit' ? editFromModal : deleteFromModal;
        const okFunction = modalAction === 'add' ? addFromModal : modalActionReq;
        return (React.createElement("div", { className: "form-actions" },
            React.createElement("button", { className: "button button-green mr-2", onClick: okFunction }, t('OK')),
            React.createElement("button", { className: "button button-green", onClick: onHideModal }, t('CANCEL'))));
    };
    const renderModals = () => {
        return (React.createElement(CargomaticModal, { show: showModal, onHide: onHideModal, title: t('ACTION_MAPPING', {
                action: titleize(t(sluggify(modalAction))),
            }), body: renderModalComponent(), footer: renderModalFooter(), location: "middle" }));
    };
    const renderHeading = (key, level, path, addFunction) => {
        return (React.createElement("div", { className: "flex flex-row" },
            React.createElement("div", { className: `ml-${level * 4} mt-2 font-bold` }, t(sluggify(key + 's'))),
            React.createElement("div", { className: "pl-2 cursor-pointer mt-2", onClick: () => addFunction(key, path) },
                React.createElement("i", { className: "fa fa-plus text-green" }))));
    };
    const subTree = path.length ? _.get(customMapping, path) : customMapping;
    if (_.isEmpty(customMapping)) {
        return null;
    }
    if (!isBottomLevel()) {
        return [
            renderHeading(key, level, path, addConfigItem),
            Object.keys(subTree[key]).map((nextKey) => {
                const newPath = [...path, key, nextKey];
                const pathString = newPath.join('.');
                return (React.createElement("div", { key: pathString, className: `ml-${level * 4} -mb-2` },
                    React.createElement("div", { className: "flex flex-row" },
                        React.createElement("span", { className: "flex items-center" }, nextKey),
                        React.createElement(EditButton, { onClick: () => editConfigItem(key, newPath) }),
                        React.createElement(DeleteButton, { onClick: () => deleteConfigItem(key, newPath) })),
                    React.createElement(RoutingLevel, { level: level + 1, path: newPath, mapping: mapping, t: t })));
            }),
            renderModals(),
        ];
    }
    // This is the bottom level that maps to an account
    return [
        renderHeading(key, level, path, addConfigItem),
        Object.keys(subTree[key]).map((nextKey) => {
            const newPath = [...path, key, nextKey];
            const pathString = newPath.join('.');
            const account = subTree[key][nextKey];
            setAccountName(account);
            return (React.createElement("div", { key: pathString, className: `ml-${level * 4} -mb-2` },
                React.createElement("div", { className: "flex flex-row" },
                    React.createElement("span", { className: "flex items-center" },
                        nextKey,
                        ": ",
                        accountNames[account] || account),
                    React.createElement(EditButton, { onClick: () => editConfigItem(key, newPath) }),
                    React.createElement(DeleteButton, { onClick: () => deleteConfigItem(key, newPath) }))));
        }),
        renderModals(),
    ];
};
export default ShipperRoutingView;
